import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui";
import {
  type AttributeReviewValueFixInput,
  useAttributeReviewChoosePoolMutation,
  useAttributeReviewFieldMappingsQuery,
  useAttributeReviewRefreshNamesMutation,
} from "@/graphql/generated";
import type { AttributeReviewDraftSelection } from "@/hooks/useAttributeReview.types";
import { draftAfterPoolReply } from "@/lib/attributeReview/shownAnswer";

type Row = { sourceFieldId: string };
type ItemsData = { attributeReviewFieldMappings?: { items: Row[] } | null };

export function useRowDecision(
  draft: AttributeReviewDraftSelection | undefined,
  onChange: (next: AttributeReviewDraftSelection | null) => void,
) {
  const kind = draft?.kind ?? null;
  const leaf = draft?.leaf ?? null;
  const fixList = useMemo(() => draft?.values ?? [], [draft?.values]);
  const fixes = useMemo(
    () => Object.fromEntries(fixList.map((fix) => [fix.storeValue, fix])),
    [fixList],
  );
  const save = (next: AttributeReviewDraftSelection) =>
    onChange(
      next.kind || next.leaf || next.pool || next.values?.length
        ? { ...next, values: next.values?.length ? next.values : undefined }
        : null,
    );
  return {
    kind,
    setKind: (next: { kind: string }) => save({ kind: next.kind }),
    leaf,
    setLeaf: (next: string | null) =>
      save({
        kind: kind ?? undefined,
        leaf: next ?? undefined,
      }),
    fixes,
    setFix: (fix: AttributeReviewValueFixInput) =>
      save({
        kind: kind ?? undefined,
        leaf: leaf ?? undefined,
        pool: draft?.pool,
        values: [
          ...fixList.filter((f) => f.storeValue !== fix.storeValue),
          fix,
        ],
      }),
    undoFix: (storeValue: string) =>
      save({
        kind: kind ?? undefined,
        leaf: leaf ?? undefined,
        pool: draft?.pool,
        values: fixList.filter((f) => f.storeValue !== storeValue),
      }),
  };
}

export function useRowActions(
  sessionId: string,
  sourceFieldId: string,
  draft: AttributeReviewDraftSelection | undefined,
  onChange: (next: AttributeReviewDraftSelection | null) => void,
) {
  const queryClient = useQueryClient();
  const pasteRow = (changed: Row) => {
    const paste = (data: ItemsData | undefined) =>
      data?.attributeReviewFieldMappings
        ? {
            ...data,
            attributeReviewFieldMappings: {
              ...data.attributeReviewFieldMappings,
              items: data.attributeReviewFieldMappings.items.map((row) =>
                row.sourceFieldId === changed.sourceFieldId
                  ? { ...row, ...changed }
                  : row,
              ),
            },
          }
        : data;
    queryClient.setQueriesData<ItemsData>(
      { queryKey: useAttributeReviewFieldMappingsQuery.getKey({ sessionId }) },
      paste,
    );
  };
  const onError = (error: unknown) =>
    showToast.error(
      "Not saved",
      error instanceof Error ? error.message : "Please try again.",
    );
  const afterPoolReply = (
    reply: Row & Parameters<typeof draftAfterPoolReply>[1],
    sent: { kind?: string | null; leaf?: string | null },
    keepFixes: boolean,
  ) => {
    pasteRow(reply);
    if (draft?.kind !== (sent.kind ?? undefined)) return;
    if (draft?.leaf !== (sent.leaf ?? undefined)) return;
    const next = draftAfterPoolReply(draft ?? {}, reply, keepFixes);
    onChange(Object.keys(next).length ? next : null);
  };
  const refresh = useAttributeReviewRefreshNamesMutation({
    onSuccess: (data, vars) =>
      vars.what === "leaf"
        ? pasteRow(data.attributeReviewRefreshNames)
        : afterPoolReply(
            data.attributeReviewRefreshNames,
            vars,
            vars.what === "values",
          ),
    onError,
  });
  const choose = useAttributeReviewChoosePoolMutation({
    onSuccess: (data, vars) =>
      afterPoolReply(data.attributeReviewChoosePool, vars, false),
    onError,
  });
  return {
    busy: refresh.isPending || choose.isPending,
    refreshNames: (what: "leaf" | "pool" | "values") =>
      refresh.mutate({
        sessionId,
        sourceFieldId,
        what,
        kind: draft?.kind,
        leaf: draft?.leaf,
      }),
    choosePool: (pool: string) =>
      choose.mutate({
        sessionId,
        sourceFieldId,
        pool,
        kind: draft?.kind,
        leaf: draft?.leaf,
      }),
  };
}
